(() => {
  'use strict';

  const existingLoader = window.TANProofStoriesLoader;
  if (existingLoader?.scan) {
    existingLoader.scan(document);
    return;
  }

  const ROOT_SELECTOR = 'tan-proof-stories[data-proof-runtime-src]';
  const ROOT_STATES = new WeakMap();
  const STATS_FRAME_INTERVAL = 30;
  let runtimeRequest = null;

  const runtimeReady = () => Boolean(window.customElements?.get('tan-proof-stories'));
  const absoluteUrl = (value) => {
    try {
      return new URL(String(value || ''), document.baseURI).href;
    } catch (_error) {
      return '';
    }
  };

  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
  const easeOutCubic = (value) => 1 - Math.pow(1 - value, 3);
  const statItems = (root) => Array.from(root.querySelectorAll('[data-proof-stat]')).map(
    (stat, index) => ({
      stat,
      number: stat.querySelector('[data-proof-number]'),
      target: clamp(Number(stat.dataset.proofTarget) || 0, 0, 100),
      delay: index * 120,
    })
  );
  const setStatValue = (item, value) => {
    const rounded = Math.round(value);
    item.stat.style.setProperty('--tan-proof-progress', String(value));
    if (item.number && item.number.textContent !== String(rounded)) {
      item.number.textContent = String(rounded);
    }
  };
  const setFinalStats = (items) => items.forEach((item) => setStatValue(item, item.target));

  const setupStats = (root, state) => {
    const items = statItems(root);
    if (!items.length) return;

    // Keep counters independent so preparing the reels does not reset them.
    root.dataset.proofStatsManaged = 'loader';
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (reducedMotion.matches || !('IntersectionObserver' in window)) {
      setFinalStats(items);
      return;
    }

    state.statsMotionQuery = reducedMotion;
    state.statsMotionHandler = (event) => {
      if (!event.matches) return;
      state.statsObserver?.disconnect();
      state.statsObserver = null;
      if (state.statsFrame) cancelAnimationFrame(state.statsFrame);
      state.statsFrame = 0;
      state.statsStarted = true;
      setFinalStats(items);
    };
    reducedMotion.addEventListener?.('change', state.statsMotionHandler);

    items.forEach((item) => setStatValue(item, 0));
    const stats = root.querySelector('.tan-story-proof__stats');
    if (!stats) return;

    const startAnimation = () => {
      if (state.statsStarted || state.abortController.signal.aborted) return;
      state.statsStarted = true;
      const duration = 1180;
      const startedAt = performance.now();
      let previousPaint = startedAt - STATS_FRAME_INTERVAL;

      const frame = (time) => {
        state.statsFrame = 0;
        if (state.abortController.signal.aborted || !root.isConnected) return;
        if (time - previousPaint < STATS_FRAME_INTERVAL) {
          state.statsFrame = requestAnimationFrame(frame);
          return;
        }
        previousPaint = time;
        let complete = true;
        items.forEach((item) => {
          const progress = clamp((time - startedAt - item.delay) / duration);
          if (progress < 1) complete = false;
          setStatValue(item, item.target * easeOutCubic(progress));
        });
        if (!complete) state.statsFrame = requestAnimationFrame(frame);
      };

      state.statsFrame = requestAnimationFrame(frame);
    };

    state.statsObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        state.statsObserver?.disconnect();
        state.statsObserver = null;
        startAnimation();
      },
      { threshold: 0.2, rootMargin: '0px 0px -10% 0px' }
    );
    state.statsObserver.observe(stats);
  };

  const loadRuntime = (root) => {
    if (runtimeReady()) return Promise.resolve();
    if (runtimeRequest) return runtimeRequest;

    const source = absoluteUrl(root.dataset.proofRuntimeSrc);
    if (!source) return Promise.reject(new Error('PROOF_STORIES_RUNTIME_URL_MISSING'));

    runtimeRequest = new Promise((resolve, reject) => {
      const pending = Array.from(document.scripts).find(
        (script) => absoluteUrl(script.src) === source
      );
      const script = pending || document.createElement('script');
      let timeoutId = 0;
      const cleanup = () => {
        window.clearTimeout(timeoutId);
        script.removeEventListener('load', handleLoad);
        script.removeEventListener('error', handleError);
      };
      const fail = (message) => {
        cleanup();
        if (!pending || script.dataset.tanProofRuntime === 'true') script.remove();
        runtimeRequest = null;
        reject(new Error(message));
      };
      const handleLoad = () => {
        cleanup();
        if (runtimeReady()) {
          resolve();
          return;
        }
        fail('PROOF_STORIES_RUNTIME_INITIALIZATION_FAILED');
      };
      const handleError = () => fail('PROOF_STORIES_RUNTIME_LOAD_FAILED');

      script.addEventListener('load', handleLoad, { once: true });
      script.addEventListener('error', handleError, { once: true });
      timeoutId = window.setTimeout(
        () => fail('PROOF_STORIES_RUNTIME_LOAD_TIMEOUT'),
        15000
      );

      if (!pending) {
        script.src = source;
        script.async = true;
        script.dataset.tanProofRuntime = 'true';
        document.head.append(script);
      }
    });

    return runtimeRequest;
  };

  const initialPlayIcons = new WeakMap();
  const syncEarlyButton = (video) => {
    const button = video.closest('[data-proof-media]')?.querySelector('[data-proof-play]');
    if (!button) return;
    const playing = !video.paused && !video.ended;
    button.classList.toggle('is-playing', playing);
    button.setAttribute('aria-pressed', String(playing));
    button.setAttribute('aria-label', playing ? button.dataset.pauseLabel : button.dataset.playLabel);
    const icon = button.querySelector('[data-proof-play-icon]');
    if (icon) {
      if (!initialPlayIcons.has(icon)) initialPlayIcons.set(icon, icon.innerHTML);
      icon.innerHTML = playing
        ? '<svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><rect x="5" y="3" width="5" height="18" rx="1"/><rect x="14" y="3" width="5" height="18" rx="1"/></svg>'
        : initialPlayIcons.get(icon);
    }
  };
  const playBeforeRuntime = (root, button) => {
    const video = button.closest('[data-proof-media]')?.querySelector('[data-proof-video]');
    if (!video || video.dataset.proofPlayPending === 'true') return;
    if (!video.paused) {
      video.pause();
      syncEarlyButton(video);
      return;
    }
    root.querySelectorAll('[data-proof-video]').forEach((other) => {
      if (other !== video) {
        other.pause();
        syncEarlyButton(other);
      }
    });
    const sources = video.querySelectorAll('source[data-src]');
    if (sources.length) {
      sources.forEach((source) => {
        source.src = source.dataset.src;
        source.removeAttribute('data-src');
      });
      video.dataset.proofHydrated = 'true';
      video.load();
    }
    video.dataset.proofPlayPending = 'true';
    button.setAttribute('aria-busy', 'true');
    const finish = () => {
      delete video.dataset.proofPlayPending;
      button.removeAttribute('aria-busy');
      syncEarlyButton(video);
    };
    try {
      Promise.resolve(video.play()).then(finish, finish);
    } catch (_error) {
      finish();
    }
  };

  const attach = (root) => {
    if (!(root instanceof HTMLElement) || ROOT_STATES.has(root)) return;

    const abortController = new AbortController();
    const state = {
      abortController,
      observer: null,
      statsObserver: null,
      statsFrame: 0,
      statsStarted: false,
      statsMotionQuery: null,
      statsMotionHandler: null,
    };
    ROOT_STATES.set(root, state);
    setupStats(root, state);
    const syncBeforeRuntime = (event) => {
      if (runtimeReady() || !(event.target instanceof HTMLVideoElement)) return;
      syncEarlyButton(event.target);
    };
    ['playing', 'pause', 'ended', 'error'].forEach((type) => {
      root.addEventListener(type, syncBeforeRuntime, { capture: true, signal: abortController.signal });
    });

    const activate = () => {
      if (abortController.signal.aborted || !root.isConnected) return Promise.resolve();
      state.observer?.disconnect();
      // Warm the runtime as the section approaches, even during vertical scroll.
      // Video sources stay untouched until the visitor presses play.
      return loadRuntime(root);
    };
    root.addEventListener('pointerenter', () => void activate().catch(() => {}), {
      passive: true,
      once: true,
      signal: abortController.signal,
    });
    root.addEventListener('focusin', () => void activate().catch(() => {}), {
      capture: true,
      once: true,
      signal: abortController.signal,
    });
    root.addEventListener(
      'click',
      (event) => {
        if (runtimeReady()) return;
        const control = event.target.closest?.('[data-proof-play]');
        if (!control || !root.contains(control)) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        playBeforeRuntime(root, control);
        void activate().catch(() => {});
      },
      { capture: true, signal: abortController.signal }
    );

    if (window.Shopify?.designMode || !('IntersectionObserver' in window)) {
      void activate().catch(() => {});
      return;
    }

    state.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) void activate().catch(() => {});
      },
      { rootMargin: '600px 0px', threshold: 0 }
    );
    state.observer.observe(root);
  };

  const rootsWithin = (scope) => {
    if (!(scope instanceof Element || scope instanceof Document)) return [];
    const roots = Array.from(scope.querySelectorAll(ROOT_SELECTOR));
    if (scope instanceof Element && scope.matches(ROOT_SELECTOR)) roots.unshift(scope);
    return roots;
  };
  const scan = (scope = document) => rootsWithin(scope).forEach(attach);
  const disconnect = (root) => {
    const state = ROOT_STATES.get(root);
    state?.observer?.disconnect();
    state?.statsObserver?.disconnect();
    if (state?.statsFrame) cancelAnimationFrame(state.statsFrame);
    state?.statsMotionQuery?.removeEventListener?.('change', state.statsMotionHandler);
    state?.abortController?.abort();
    ROOT_STATES.delete(root);
  };

  window.TANProofStoriesLoader = Object.freeze({ scan });
  scan(document);
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    rootsWithin(event.target).forEach(disconnect);
  });
})();
